import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiRequestError, fetchProgrammeOverview, fetchProgrammeState } from '../api/client.js';
import {
  AUTONOMY_PRIMER_ARITHMETIC,
  AUTONOMY_PRIMER_GOOD_BAD,
  AUTONOMY_PRIMER_PLAIN,
  CANONICAL_STAGES,
  CONTENT_KIND_LABEL,
  COUNT_KEY_LINE,
  NEVER_PUBLISHED_COPY,
  STRUCTURAL_NON_BLOCKING,
  autonomyMetric,
  buildOverviewMatrix,
  collectFreshnessIssues,
  filterAutonomyRows,
  filterDefects,
  filterStructuralRows,
  formatAutonomyVerdict,
  formatFloor,
  formatInterval,
  formatMeasuredNumber,
  formatRateRange,
  formatRemaining,
  formatVerification,
  joinGapsAndTargets,
  levelsFromOverview,
  parseProgrammeLevel,
  readProgrammeStateDocument,
  scopeLabel,
  type CountView,
  type FreshnessIssue,
  type JoinedGapRow,
  type OverviewMatrixRow,
} from '../lib/programmeView.js';
import { AUTONOMY_HEADLINE } from '../types/programme.js';
import type {
  DefectDimension,
  DefectExposedBy,
  LevelId,
  ProgrammeLevelFilter,
  ProgrammeOverview,
  ProgrammeStateDocument,
  ProgrammeStateGetResponse,
  ProgrammeStateResponse,
} from '../types/programme.js';

type ViewId = 'overview' | 'gaps' | 'structural' | 'autonomy' | 'defects';

const VIEWS: ReadonlyArray<{ id: ViewId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'gaps', label: 'Gaps' },
  { id: 'structural', label: 'Structural' },
  { id: 'autonomy', label: 'Autonomy' },
  { id: 'defects', label: 'Defect Defence Register' },
];

function isAbort(reason: unknown): boolean {
  return (
    (reason instanceof DOMException && reason.name === 'AbortError')
    || (reason instanceof Error && reason.name === 'AbortError')
  );
}

function errorMessage(reason: unknown): string {
  if (reason instanceof ApiRequestError || reason instanceof Error) {
    return reason.message;
  }
  return 'Unable to load programme dashboard';
}

export function ProgrammeDashboard() {
  const [searchParams] = useSearchParams();
  const level = parseProgrammeLevel(searchParams.get('level'));
  const [view, setView] = useState<ViewId>('overview');
  const [overviewByLevel, setOverviewByLevel] = useState<Partial<Record<ProgrammeLevelFilter, ProgrammeOverview>>>({});
  const [overviewErrorByLevel, setOverviewErrorByLevel] = useState<Partial<Record<ProgrammeLevelFilter, string>>>({});
  const [stateResponse, setStateResponse] = useState<ProgrammeStateResponse | null>(null);
  const [stateError, setStateError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const requested = level;
    setOverviewErrorByLevel((prev) => {
      if (!(requested in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[requested];
      return next;
    });
    void fetchProgrammeOverview(requested, controller.signal)
      .then((value) => {
        setOverviewByLevel((prev) => ({ ...prev, [requested]: value }));
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted || isAbort(reason)) {
          return;
        }
        setOverviewErrorByLevel((prev) => ({ ...prev, [requested]: errorMessage(reason) }));
      });
    return () => {
      controller.abort();
    };
  }, [level]);

  useEffect(() => {
    let cancelled = false;
    void fetchProgrammeState()
      .then((value) => {
        if (!cancelled) {
          setStateResponse(value);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setStateError(errorMessage(reason));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const overview = overviewByLevel[level];
  const overviewError = overviewErrorByLevel[level];
  const document = useMemo(() => {
    if (!stateResponse || stateResponse.state === null) {
      return null;
    }
    return readProgrammeStateDocument(stateResponse.state);
  }, [stateResponse]);
  const stateAbsent = stateResponse !== null && stateResponse.state === null;
  const statePresent = stateResponse !== null && stateResponse.state !== null
    ? (stateResponse as ProgrammeStateGetResponse)
    : null;

  if (!overview && !overviewError) {
    return (
      <main className="flex-1 overflow-y-auto bg-background-light" data-testid="programme-dashboard">
        <StatePanel message="Loading programme dashboard..." testId="programme-loading" tone="neutral" />
      </main>
    );
  }

  if (overviewError && !overview) {
    return (
      <main className="flex-1 overflow-y-auto bg-background-light" data-testid="programme-dashboard">
        <StatePanel message={overviewError} testId="programme-error" tone="error" />
      </main>
    );
  }

  if (!overview) {
    return (
      <main className="flex-1 overflow-y-auto bg-background-light" data-testid="programme-dashboard">
        <StatePanel message="Loading programme dashboard..." testId="programme-loading" tone="neutral" />
      </main>
    );
  }

  const scanComplete = overview.scan.complete;
  const freshness = collectFreshnessIssues({
    nowMs: Date.now(),
    liveMeasuredAt: overview.measuredAt,
    state: stateResponse,
    document,
  });

  return (
    <main className="flex-1 overflow-y-auto bg-background-light" data-testid="programme-dashboard">
      <div className="space-y-6 p-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Programme</h1>
            <p className="text-sm text-slate-500" data-testid="programme-scope">
              {scopeLabel(overview, level)}
              {overview.grantedCourseKeys.length > 0
                ? ` · ${overview.grantedCourseKeys.join(', ')}`
                : ''}
            </p>
            <p className="font-mono text-[11px] text-slate-400" data-testid="programme-overview-digest">
              {overview.stateDocumentDigest ?? 'none'}
            </p>
          </div>
          <TimesStrip
            liveMeasuredAt={overview.measuredAt}
            state={statePresent}
            document={document}
          />
        </header>

        <p className="text-xs font-medium text-slate-600" data-testid="programme-count-key">
          {COUNT_KEY_LINE}
        </p>

        {freshness.length > 0 ? <FreshnessBanner issues={freshness} state={statePresent} document={document} /> : null}

        {!scanComplete ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-950"
            data-testid="programme-incomplete-scan"
          >
            Live scan is incomplete
            {overview.scan.reason ? ` (${overview.scan.reason})` : ''}: live counts are lower bounds
            and autonomy verdicts are suppressed.
          </div>
        ) : null}

        {overview.autonomyRead.capped ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-950"
            data-testid="programme-autonomy-capped"
          >
            Review-event reads were capped at {overview.autonomyRead.cap} (read {overview.autonomyRead.eventsRead}
            {' '}of population {overview.autonomyRead.population}). The refusal range is already widened.
          </div>
        ) : null}

        {stateAbsent ? <NeverPublishedPanel /> : null}
        {stateError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800">
            Programme state document could not be read: {stateError}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-px" role="tablist">
          {VIEWS.map((entry) => {
            const active = view === entry.id;
            return (
              <button
                className={
                  active
                    ? '-mb-px border-b-2 border-primary px-3 py-2 text-sm font-bold text-primary'
                    : 'px-3 py-2 text-sm font-medium text-slate-500 hover:text-primary'
                }
                data-testid={`programme-view-tab-${entry.id}`}
                key={entry.id}
                onClick={() => setView(entry.id)}
                role="tab"
                type="button"
              >
                {entry.label}
              </button>
            );
          })}
        </div>

        {view === 'overview' ? (
          <OverviewView
            document={document}
            level={level}
            overview={overview}
            scanComplete={scanComplete}
          />
        ) : null}
        {view === 'gaps' ? (
          <GapsView document={document} level={level} stateAbsent={stateAbsent} stateError={stateError} />
        ) : null}
        {view === 'structural' ? (
          <StructuralView document={document} level={level} stateAbsent={stateAbsent} stateError={stateError} />
        ) : null}
        {view === 'autonomy' ? (
          <AutonomyView
            controls={document?.controls ?? null}
            level={level}
            overview={overview}
            scanComplete={scanComplete}
          />
        ) : null}
        {view === 'defects' ? (
          <DefectsView document={document} stateAbsent={stateAbsent} stateError={stateError} />
        ) : null}
      </div>
    </main>
  );
}

function TimesStrip({
  liveMeasuredAt,
  state,
  document,
}: {
  liveMeasuredAt: string;
  state: ProgrammeStateGetResponse | null;
  document: ProgrammeStateDocument | null;
}) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-[11px] text-slate-500 sm:grid-cols-2" data-testid="programme-times">
      <div>
        <dt className="font-semibold uppercase tracking-wider">Live measuredAt</dt>
        <dd className="font-mono text-slate-700">{liveMeasuredAt}</dd>
      </div>
      <div>
        <dt className="font-semibold uppercase tracking-wider">generatedAt (publisher)</dt>
        <dd className="font-mono text-slate-700">{state?.generatedAt ?? document?.generatedAt ?? 'no data'}</dd>
      </div>
      <div>
        <dt className="font-semibold uppercase tracking-wider">publishedAt (server)</dt>
        <dd className="font-mono text-slate-700">{state?.publishedAt ?? 'no data'}</dd>
      </div>
      <div>
        <dt className="font-semibold uppercase tracking-wider">oldest source measuredAt</dt>
        <dd className="font-mono text-slate-700">{state?.oldestSourceMeasuredAt ?? 'no data'}</dd>
      </div>
    </dl>
  );
}

function FreshnessBanner({
  issues,
  state,
  document,
}: {
  issues: FreshnessIssue[];
  state: ProgrammeStateGetResponse | null;
  document: ProgrammeStateDocument | null;
}) {
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
      data-testid="programme-freshness-banner"
    >
      <p className="font-semibold">Stale or missing sources</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {issues.map((issue) => (
          <li key={`${issue.kind}:${issue.name}`}>
            {freshnessCopy(issue)}
            {issue.measuredAt ? ` · measuredAt ${issue.measuredAt}` : ''}
          </li>
        ))}
      </ul>
      {state ? (
        <p className="mt-2 font-mono text-[11px] text-amber-900">
          generatedAt {state.generatedAt} · publishedAt {state.publishedAt}
          {state.oldestSourceMeasuredAt ? ` · oldest source ${state.oldestSourceMeasuredAt}` : ''}
        </p>
      ) : null}
      {document && document.sources.length > 0 ? (
        <ul className="mt-2 space-y-0.5 font-mono text-[11px] text-amber-900">
          {document.sources.map((source) => (
            <li key={source.name}>
              {source.name}: {source.status}
              {source.measuredAt ? ` · measuredAt ${source.measuredAt}` : ''}
              {source.reason ? ` · ${source.reason}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function freshnessCopy(issue: FreshnessIssue): string {
  switch (issue.kind) {
    case 'absent_document':
      return 'Programme state document is missing';
    case 'unread_source':
      return `Expected source unread: ${issue.name}`;
    case 'stale_source':
      return `Source older than 24 hours: ${issue.name}`;
    case 'stale_live':
      return 'Live work-item scan is older than 24 hours';
    case 'incomplete_source':
      return `Source reports incomplete coverage: ${issue.name}`;
    default:
      return issue.name;
  }
}

function OverviewView({
  overview,
  document,
  level,
  scanComplete,
}: {
  overview: ProgrammeOverview;
  document: ProgrammeStateDocument | null;
  level: ProgrammeLevelFilter;
  scanComplete: boolean;
}) {
  const levels = levelsFromOverview(overview, level);
  const liveCells = level === 'all' ? overview.cells : overview.cells.filter((cell) => cell.level === level);
  const documentCells = document
    ? document.cells.filter((cell) => level === 'all' || cell.level === level)
    : [];
  const matrix = buildOverviewMatrix({
    liveCells,
    liveMeasuredAt: overview.measuredAt,
    documentCells,
    workTypes: document?.workTypes ?? [],
    levels,
  });

  const byLevel = new Map<LevelId, typeof matrix.groups>();
  for (const group of matrix.groups) {
    const list = byLevel.get(group.level) ?? [];
    list.push(group);
    byLevel.set(group.level, list);
  }

  return (
    <section className="space-y-6" data-testid="programme-view-overview">
      {[...byLevel.entries()].map(([rowLevel, groups]) => (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" key={rowLevel}>
          <div className="border-b border-slate-100 px-5 py-3">
            <h2 className="text-lg font-bold text-primary">Level {rowLevel}</h2>
            <p className="text-xs text-slate-500">
              Live and document counts sit side by side in each cell and are never added together.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs" data-testid="programme-overview-matrix">
              <thead className="bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-100 px-4 py-3">Work type</th>
                  {CANONICAL_STAGES.map((stage) => (
                    <th className="px-3 py-3" key={stage}>{stage.replaceAll('_', ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((group, index) => {
                  const kindChanged = index === 0 || groups[index - 1]?.contentKind !== group.contentKind;
                  return (
                    <GroupRows
                      group={group}
                      key={`${group.level}:${group.workType}`}
                      kindChanged={kindChanged}
                      scanComplete={scanComplete}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {matrix.groups.length === 0 ? (
        <p className="text-sm text-slate-500">No work-type occupancy to show for this level.</p>
      ) : null}

      <ResidueTable
        caption="Unmapped (taskType, op, targetType) triples"
        empty="None."
        headers={['taskType', 'op', 'targetType', 'course', 'level', 'slug', 'reason', 'count']}
        rows={overview.unmapped.map((row) => [
          row.taskType,
          row.op,
          row.targetType,
          row.courseKey,
          row.level ?? '—',
          row.slug ?? '—',
          row.reason,
          String(row.count),
        ])}
        testId="programme-unmapped"
      />
      <ResidueTable
        caption="Unmapped statuses"
        empty="None."
        headers={['status', 'count']}
        rows={overview.unmappedStatuses.map((row) => [row.status, String(row.count)])}
        testId="programme-unmapped-statuses"
      />
      <ResidueTable
        caption="Unknown courses"
        empty="None."
        headers={['courseKey', 'count', 'reason']}
        rows={overview.unknownCourses.map((row) => [row.courseKey, String(row.count), row.reason])}
        testId="programme-unknown-courses"
      />

      {document && document.notes.length > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-700">
          <h3 className="text-sm font-bold text-primary">Notes</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {/* Keyed by index, not by the note text: two notes CAN legitimately be identical
                (two screens carrying the same verbatim caveat), and a content-keyed list then
                duplicates React keys, which React says may duplicate or omit children. Observed
                in the console on the first real published document, 2026-09-10. */}
            {document.notes.map((note, index) => (
              <li key={`note-${index}`}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function GroupRows({
  group,
  kindChanged,
  scanComplete,
}: {
  group: { contentKind: OverviewMatrixRow['contentKind']; workTypeLabel: string; operation: OverviewMatrixRow['operation']; rows: OverviewMatrixRow[] };
  kindChanged: boolean;
  scanComplete: boolean;
}) {
  return (
    <>
      {kindChanged ? (
        <tr className="bg-slate-50">
          <td
            className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500"
            colSpan={CANONICAL_STAGES.length + 1}
          >
            {CONTENT_KIND_LABEL[group.contentKind]}
          </td>
        </tr>
      ) : null}
      {group.rows.map((row) => {
        const isTotal = row.kind === 'level_total';
        return (
          <tr
            className={isTotal ? 'bg-slate-50/80' : 'bg-white'}
            data-testid={isTotal ? 'programme-level-total-row' : 'programme-section-row'}
            key={row.id}
          >
            <td className="sticky left-0 z-10 whitespace-nowrap bg-inherit px-4 py-3">
              <div className="font-semibold text-slate-800">{group.workTypeLabel}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{row.operation}</div>
              <div className="mt-0.5 text-[10px] font-medium text-slate-500">
                {isTotal ? 'Level total' : `Section ${row.section}`}
              </div>
            </td>
            {CANONICAL_STAGES.map((stage) => {
              const pair = row.cells[stage] ?? { live: { present: false, count: null, unit: null, source: null, measuredAt: null, tentative: false }, document: { present: false, count: null, unit: null, source: null, measuredAt: null, tentative: false } };
              return (
                <td className="align-top px-3 py-3" key={stage}>
                  <CountPair document={pair.document} live={pair.live} scanComplete={scanComplete} />
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

function CountPair({
  live,
  document,
  scanComplete,
}: {
  live: CountView;
  document: CountView;
  scanComplete: boolean;
}) {
  return (
    <div className="min-w-[7rem] space-y-1">
      <MeasuredCount incomplete={!scanComplete} view={live} />
      <div className="border-t border-slate-100 pt-1">
        <MeasuredCount incomplete={false} view={document} />
      </div>
    </div>
  );
}

function MeasuredCount({ view, incomplete }: { view: CountView; incomplete: boolean }) {
  const text = formatMeasuredNumber({
    present: view.present,
    value: view.count,
    incomplete: incomplete && view.present && view.source === 'live',
  });
  const isNoData = text === 'no data';
  const isZero = text === '0' || text === '≥0';
  return (
    <div data-testid="programme-count">
      <span
        className={
          isNoData
            ? 'italic text-amber-800'
            : isZero
              ? 'font-mono text-base font-bold tabular-nums text-slate-900'
              : 'font-mono text-base font-bold tabular-nums text-primary'
        }
      >
        {text}
      </span>
      {view.present ? (
        <div className="text-[10px] leading-tight text-slate-500">
          {view.unit} · {view.source} · {view.measuredAt}
          {view.tentative ? ' · tentative' : ''}
        </div>
      ) : (
        <div className="text-[10px] italic text-amber-700">unread</div>
      )}
    </div>
  );
}

function ResidueTable({
  caption,
  headers,
  rows,
  empty,
  testId,
}: {
  caption: string;
  headers: string[];
  rows: string[][];
  empty: string;
  testId: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="border-b border-slate-100 px-5 py-3">
        <h3 className="text-sm font-bold text-primary">{caption}</h3>
        <p className="text-[11px] text-slate-500">Honest residue — never hidden, never folded into a neighbour.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              {headers.map((header) => (
                <th className="px-4 py-2" key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td className="px-4 py-3 italic text-slate-400" colSpan={headers.length}>{empty}</td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${testId}:${index}`}>
                  {row.map((cell, cellIndex) => (
                    <td className="px-4 py-2 text-slate-700" key={`${index}:${cellIndex}`}>{cell}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GapsView({
  document,
  level,
  stateAbsent,
  stateError,
}: {
  document: ProgrammeStateDocument | null;
  level: ProgrammeLevelFilter;
  stateAbsent: boolean;
  stateError: string;
}) {
  if (stateAbsent) {
    return (
      <section data-testid="programme-view-gaps">
        <NeverPublishedPanel />
      </section>
    );
  }
  if (stateError && !document) {
    return (
      <section data-testid="programme-view-gaps">
        <StatePanel message={stateError} testId="programme-gaps-error" tone="error" />
      </section>
    );
  }
  if (!document) {
    return (
      <section data-testid="programme-view-gaps">
        <StatePanel message="Loading programme state..." testId="programme-gaps-loading" tone="neutral" />
      </section>
    );
  }

  const rows = joinGapsAndTargets({
    gaps: document.gaps,
    targets: document.targets,
    workTypes: document.workTypes,
    filter: level,
  });

  return (
    <section className="space-y-4" data-testid="programme-view-gaps">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No gap or target rows for this level.</p>
      ) : null}
      {rows.map((row) => (
        <GapCard key={`${row.level}:${row.workType}:${row.section ?? 'total'}`} row={row} />
      ))}
    </section>
  );
}

function GapCard({ row }: { row: JoinedGapRow }) {
  const floorLabel = formatFloor(row.floor);
  const remainingLabel = formatRemaining(row.remaining, row.remainingReason);
  const notRequired = row.floor === 0;
  const targetUnknown = row.floor === null || row.floor === undefined;
  const notMeasured = row.remaining === null;

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="programme-gap-row">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-primary">
            Level {row.level} · {row.workTypeLabel}
            {row.section ? ` · ${row.section}` : ''}
          </h3>
          <p className="text-xs text-slate-500">{row.basis}</p>
        </div>
        {row.priority !== null ? (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-600">
            priority {row.priority}
          </span>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Fact
          label="Remaining"
          tone={notMeasured ? 'amber' : 'default'}
          value={remainingLabel}
          meta={`${row.unit} · ${row.source} · ${row.measuredAt}`}
        />
        <Fact
          label="Floor"
          tone={notRequired ? 'teal' : targetUnknown ? 'amber' : 'default'}
          value={floorLabel}
          meta={row.targetSource && row.targetMeasuredAt
            ? `${row.unit} · ${row.targetSource} · ${row.targetMeasuredAt}`
            : 'no target row'}
        />
        <Fact
          label="Full"
          tone="default"
          value={row.full === null || row.full === undefined ? 'no data' : String(row.full)}
          meta={row.targetSource && row.targetMeasuredAt
            ? `${row.unit} · ${row.targetSource} · ${row.targetMeasuredAt}`
            : 'no data'}
        />
      </div>

      <GapBar row={row} />

      <dl className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
        {row.note ? (
          <div>
            <dt className="font-semibold text-slate-500">Note</dt>
            <dd>{row.note}</dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold text-slate-500">Unmapped requirements</dt>
          <dd>
            {row.unmappedRequirements === null || row.unmappedRequirements === undefined
              ? 'no data'
              : String(row.unmappedRequirements)}
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-500">Tentative discard / candidates</dt>
          <dd>{row.tentative === null ? 'no data' : String(row.tentative)}</dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-500">Discarded (confirmed)</dt>
          <dd>{row.discarded === null ? 'no data' : String(row.discarded)}</dd>
        </div>
      </dl>
    </article>
  );
}

function GapBar({ row }: { row: JoinedGapRow }) {
  if (row.floor === 0) {
    return null;
  }
  const full = row.full ?? 0;
  if (full <= 0 || row.remaining === null) {
    return null;
  }
  const tentative = row.tentative ?? 0;
  const solid = Math.max(0, row.remaining - tentative);
  const solidPct = (solid / full) * 100;
  const tentPct = (Math.min(tentative, row.remaining) / full) * 100;
  return (
    <div className="mt-4">
      <div className="h-3 w-full overflow-hidden rounded bg-slate-200">
        <div className="flex h-full">
          <div className="h-full bg-primary" style={{ width: `${solidPct}%` }} />
          {tentPct > 0 ? (
            <div
              className="h-full"
              data-testid="gap-tentative-hatch"
              style={{
                width: `${tentPct}%`,
                background: 'repeating-linear-gradient(-45deg, #d97706, #d97706 3px, #fde68a 3px, #fde68a 6px)',
              }}
            />
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-[10px] text-slate-500">
        Scale is full={full} {row.unit}. Hatched segment is tentative discard.
      </p>
    </div>
  );
}

function Fact({
  label,
  value,
  meta,
  tone,
}: {
  label: string;
  value: string;
  meta: string;
  tone: 'default' | 'amber' | 'teal';
}) {
  const valueClass =
    tone === 'amber'
      ? 'italic text-amber-800'
      : tone === 'teal'
        ? 'font-semibold text-teal-800'
        : value === '0'
          ? 'font-mono text-xl font-bold text-slate-900'
          : 'font-mono text-xl font-bold text-primary';
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={valueClass}>{value}</div>
      <div className="text-[10px] text-slate-500">{meta}</div>
    </div>
  );
}

function StructuralView({
  document,
  level,
  stateAbsent,
  stateError,
}: {
  document: ProgrammeStateDocument | null;
  level: ProgrammeLevelFilter;
  stateAbsent: boolean;
  stateError: string;
}) {
  if (stateAbsent) {
    return (
      <section data-testid="programme-view-structural">
        <NeverPublishedPanel />
      </section>
    );
  }
  if (stateError && !document) {
    return (
      <section data-testid="programme-view-structural">
        <StatePanel message={stateError} testId="programme-structural-error" tone="error" />
      </section>
    );
  }
  if (!document) {
    return (
      <section data-testid="programme-view-structural">
        <StatePanel message="Loading programme state..." testId="programme-structural-loading" tone="neutral" />
      </section>
    );
  }

  const rows = filterStructuralRows(document.structural.byLevel, level);

  return (
    <section className="space-y-4" data-testid="programme-view-structural">
      <p className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700">
        {STRUCTURAL_NON_BLOCKING}
      </p>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Level</th>
              <th className="px-4 py-3">Work type</th>
              <th className="px-4 py-3">Proposed</th>
              <th className="px-4 py-3">Approved</th>
              <th className="px-4 py-3">Executed</th>
              <th className="px-4 py-3">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 italic text-slate-400" colSpan={6}>No structural rows for this level.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr data-testid="programme-structural-row" key={`${row.level}:${row.workType}`}>
                  <td className="px-4 py-3 font-semibold">Level {row.level}</td>
                  <td className="px-4 py-3">{row.workType}</td>
                  <td className="px-4 py-3">
                    <NullableCount
                      measuredAt={row.measuredAt}
                      source={row.source}
                      unit="work_items"
                      value={row.proposed}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <NullableCount
                      measuredAt={row.measuredAt}
                      source={row.source}
                      unit="work_items"
                      value={row.approved}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <NullableCount
                      measuredAt={row.measuredAt}
                      source={row.source}
                      unit="work_items"
                      value={row.executed}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-500">
                    {row.source} · {row.measuredAt}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NullableCount({
  value,
  unit,
  source,
  measuredAt,
}: {
  value: number | null;
  unit: string;
  source: string;
  measuredAt: string;
}) {
  const present = value !== null;
  return (
    <div data-testid="programme-count">
      <span className={present ? (value === 0 ? 'font-mono font-bold text-slate-900' : 'font-mono font-bold text-primary') : 'italic text-amber-800'}>
        {formatMeasuredNumber({ present, value })}
      </span>
      {present ? (
        <div className="text-[10px] text-slate-500">{unit} · {source} · {measuredAt}</div>
      ) : (
        <div className="text-[10px] italic text-amber-700">unread</div>
      )}
    </div>
  );
}

function AutonomyView({
  overview,
  level,
  scanComplete,
  controls,
}: {
  overview: ProgrammeOverview;
  level: ProgrammeLevelFilter;
  scanComplete: boolean;
  controls: ProgrammeStateDocument['controls'];
}) {
  const rows = filterAutonomyRows(overview.autonomy, level);
  const firstWithInterval = rows.find((row) => row.interval !== null);

  return (
    <section className="space-y-4" data-testid="programme-view-autonomy">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-primary" data-testid="programme-autonomy-heading">
            {AUTONOMY_HEADLINE}
          </h2>
          <p className="text-xs text-slate-500">
            Live reviewer outcomes · measuredAt {overview.measuredAt} · unit decisions
          </p>
        </div>
        {controls ? (
          <div
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs text-slate-700"
            data-testid="programme-autonomy-controls"
          >
            <div className="font-bold uppercase tracking-wider text-slate-500">Controls</div>
            <div>decided {controls.decided} · caught {controls.caught} · unresolved {controls.unresolved}</div>
            <div className="font-mono text-[10px] text-slate-500">
              {controls.source} · {controls.measuredAt}
            </div>
            {controls.note ? <div className="mt-1">{controls.note}</div> : null}
          </div>
        ) : null}
      </div>

      <details className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700" data-testid="programme-autonomy-primer">
        <summary className="cursor-pointer font-semibold text-primary">What the interval means</summary>
        <div className="mt-2 space-y-2 text-xs leading-relaxed">
          <p>{AUTONOMY_PRIMER_PLAIN}</p>
          <p>{AUTONOMY_PRIMER_ARITHMETIC}</p>
          <p>{AUTONOMY_PRIMER_GOOD_BAD}</p>
          {firstWithInterval?.interval ? (
            <p className="font-mono text-[11px] text-slate-500">
              First interval on this page: {formatInterval(firstWithInterval.interval)}
              {' '}({firstWithInterval.workType}, n={firstWithInterval.n})
            </p>
          ) : null}
        </div>
      </details>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-3">Level</th>
              <th className="px-3 py-3">Work type</th>
              <th className="px-3 py-3">Family</th>
              <th className="px-3 py-3">n</th>
              <th className="px-3 py-3">Refusals</th>
              <th className="px-3 py-3">Indeterminate</th>
              <th className="px-3 py-3">Range [D/N, (D+I)/N]</th>
              <th className="px-3 py-3">Clopper-Pearson</th>
              <th className="px-3 py-3">Verdict</th>
              <th className="px-3 py-3">Edit rate</th>
              <th className="px-3 py-3">Reviewers</th>
              <th className="px-3 py-3">Excluded from this measurement</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const noData = row.n === 0;
              return (
                <tr data-testid="programme-autonomy-row" key={`${row.level}:${row.workType}:${row.family}`}>
                  <td className="px-3 py-3">Level {row.level}</td>
                  <td className="px-3 py-3">{row.workType}</td>
                  <td className="px-3 py-3">{row.family}</td>
                  <td className="px-3 py-3" data-testid="autonomy-n">
                    {noData ? <span className="italic text-amber-800">no data</span> : (
                      <span className="font-mono font-bold">
                        {scanComplete ? String(row.n) : `≥${row.n}`}
                      </span>
                    )}
                    <div className="text-[10px] text-slate-500">decisions · live · {overview.measuredAt}</div>
                  </td>
                  <td className="px-3 py-3">{autonomyMetric(row.n, row.refusals)}</td>
                  <td className="px-3 py-3">{autonomyMetric(row.n, row.indeterminate)}</td>
                  <td className="px-3 py-3 font-mono">{noData ? 'no data' : formatRateRange(row.refusalRateRange)}</td>
                  <td className="px-3 py-3 font-mono">{noData ? 'no data' : formatInterval(row.interval)}</td>
                  <td className="px-3 py-3" data-testid="autonomy-verdict">
                    {formatAutonomyVerdict(row, scanComplete)}
                  </td>
                  <td className="px-3 py-3">{autonomyMetric(row.n, row.editRate, 'rate')}</td>
                  <td className="px-3 py-3">
                    {row.reviewers.length === 0
                      ? '—'
                      : row.reviewers.map((reviewer) => `${reviewer.reviewerId} (${reviewer.n})`).join(', ')}
                  </td>
                  {/*
                    WHAT WAS LEFT OUT OF THIS ROW, AND WHY. A refusal rate is only readable beside its
                    exclusions: a row reading `n: 3` over a corpus of 500 is a different fact from one
                    reading `n: 3` over a corpus of 3, and only this cell distinguishes them.
                    `not yet decided` is deliberately separate from `unknown outcome` — the reviewer
                    has not looked at those items, which is not the same as us not knowing what he did.
                  */}
                  <td className="px-3 py-3 text-[11px] text-slate-600" data-testid="autonomy-excluded">
                    {row.controlsExcluded + row.machineDecidedExcluded + row.otherReviewerExcluded + row.notYetDecided === 0
                      ? '—'
                      : [
                        row.notYetDecided > 0 ? `${row.notYetDecided} not yet decided` : null,
                        row.machineDecidedExcluded > 0 ? `${row.machineDecidedExcluded} decided by machine` : null,
                        row.otherReviewerExcluded > 0 ? `${row.otherReviewerExcluded} decided by someone else` : null,
                        row.controlsExcluded > 0 ? `${row.controlsExcluded} planted controls` : null,
                      ].filter((part) => part !== null).join(' · ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DefectsView({
  document,
  stateAbsent,
  stateError,
}: {
  document: ProgrammeStateDocument | null;
  stateAbsent: boolean;
  stateError: string;
}) {
  const [dimension, setDimension] = useState<'all' | DefectDimension>('all');
  const [exposedBy, setExposedBy] = useState<'all' | DefectExposedBy>('all');

  if (stateAbsent) {
    return (
      <section data-testid="programme-view-defects">
        <NeverPublishedPanel />
      </section>
    );
  }
  if (stateError && !document) {
    return (
      <section data-testid="programme-view-defects">
        <StatePanel message={stateError} testId="programme-defects-error" tone="error" />
      </section>
    );
  }
  if (!document) {
    return (
      <section data-testid="programme-view-defects">
        <StatePanel message="Loading programme state..." testId="programme-defects-loading" tone="neutral" />
      </section>
    );
  }

  const rows = filterDefects(document.defectRegister, { dimension, exposedBy });
  const backlog = document.unclassifiedBacklog;

  return (
    <section className="space-y-4" data-testid="programme-view-defects">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <label className="text-xs font-semibold text-slate-600">
            Dimension
            <select
              className="ml-2 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              data-testid="defect-filter-dimension"
              onChange={(event) => setDimension(event.target.value as 'all' | DefectDimension)}
              value={dimension}
            >
              <option value="all">all</option>
              <option value="academic">academic</option>
              <option value="style">style</option>
              <option value="verbosity">verbosity</option>
              <option value="structure">structure</option>
              <option value="process">process</option>
              <option value="unknown">unknown</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-600">
            Exposed by
            <select
              className="ml-2 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              data-testid="defect-filter-exposed-by"
              onChange={(event) => setExposedBy(event.target.value as 'all' | DefectExposedBy)}
              value={exposedBy}
            >
              <option value="all">all</option>
              <option value="scott">scott</option>
              <option value="gate">gate</option>
              <option value="preexisting">preexisting</option>
              <option value="mixed">mixed</option>
              <option value="unknown">unknown</option>
            </select>
          </label>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs" data-testid="defect-unclassified-backlog">
          <span className="font-bold uppercase tracking-wider text-slate-500">DC-UNCLASSIFIED backlog</span>
          <div className={backlog === null ? 'italic text-amber-800' : 'font-mono text-lg font-bold text-primary'}>
            {backlog === null ? 'no data' : String(backlog)}
          </div>
        </div>
      </div>

      {document.registerAdapterWarnings.length > 0 ? (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-950"
          data-testid="defect-adapter-warnings"
        >
          <p className="font-semibold">Register adapter warnings</p>
          <ul className="mt-1 list-disc pl-5">
            {document.registerAdapterWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-3">Class</th>
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Dimension</th>
              <th className="px-3 py-3">Exposed by</th>
              <th className="px-3 py-3">Observation</th>
              <th className="px-3 py-3">Detection</th>
              <th className="px-3 py-3">Remediation</th>
              <th className="px-3 py-3">Disposition</th>
              <th className="px-3 py-3">Verification</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td className="px-3 py-6 italic text-slate-400" colSpan={9}>No defect classes match these filters.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr data-testid="programme-defect-row" key={row.classId}>
                  <td className="px-3 py-3 font-mono font-semibold">{row.classId}</td>
                  <td className="px-3 py-3">{row.name}</td>
                  <td className="px-3 py-3">{row.dimension}</td>
                  <td className="px-3 py-3">{row.exposedBy}</td>
                  <td className="px-3 py-3">{row.observation}</td>
                  <td className="px-3 py-3">
                    <div>{row.detection.gate === '' ? <span className="italic text-slate-500">no gate recorded</span> : row.detection.gate}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{row.detection.status}</div>
                  </td>
                  <td className="max-w-[16rem] px-3 py-3">
                    {row.remediation === '' ? <span className="italic text-slate-500">no remediation recorded in the source register</span> : row.remediation}
                  </td>
                  <td className="px-3 py-3">{row.disposition}</td>
                  <td className="px-3 py-3" data-testid="defect-verification">
                    <div>{formatVerification(row.verification.kind)}</div>
                    {row.verification.on ? (
                      <div className="font-mono text-[10px] text-slate-500">{row.verification.on}</div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NeverPublishedPanel() {
  return (
    <div
      className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-6 text-sm text-amber-950"
      data-testid="programme-never-published"
    >
      <p className="font-semibold">{NEVER_PUBLISHED_COPY}</p>
      <p className="mt-1">
        Targets, gaps, structural occupancy and the defect defence register are fed by that document.
        Live work-item counts and autonomy figures on this page still come from the authoring tool.
      </p>
    </div>
  );
}

function StatePanel({
  message,
  tone,
  testId,
}: {
  message: string;
  tone: 'error' | 'neutral';
  testId: string;
}) {
  return (
    <div
      className={
        tone === 'error'
          ? 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-rose-200 bg-rose-50 px-6 text-center text-sm text-rose-800 shadow-sm'
          : 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-slate-200 bg-white px-6 text-center text-sm text-slate-500 shadow-sm'
      }
      data-testid={testId}
    >
      <span className={tone === 'neutral' ? 'animate-pulse' : ''}>{message}</span>
    </div>
  );
}
